<template>
  <div class="car-results-skeleton">
    <!-- Controls Row Skeleton -->
    <div class="controls-row-skeleton">
      <div class="left-controls-skeleton">
        <div class="grouping-toggle-skeleton">
          <div class="skeleton-button"></div>
          <div class="skeleton-button"></div>
          <div class="skeleton-button"></div>
        </div>
      </div>
      <div class="view-toggle-skeleton">
        <div class="skeleton-button"></div>
        <div class="skeleton-button"></div>
      </div>
    </div>

    <!-- Group Navigation Tabs Skeleton -->
    <div class="group-nav-tabs-skeleton">
      <div v-for="tabIndex in groupCount" :key="'tab-' + tabIndex" class="skeleton-nav-tab"></div>
    </div>

    <!-- Group Skeleton (repeated for each group) -->
    <div v-for="groupIndex in groupCount" :key="'group-' + groupIndex" class="group-skeleton">
      <!-- Group Header Skeleton -->
      <div class="group-header-skeleton">
        <div class="group-header-content">
          <b-skeleton width="30px" height="24px" :animated="true" circle></b-skeleton>
          <b-skeleton width="120px" height="20px" :animated="true"></b-skeleton>
        </div>
        <!-- <b-skeleton width="100px" height="16px" :animated="true"></b-skeleton> -->
      </div>

      <!-- List View Skeleton -->
      <div v-if="viewMode === 'list'" class="list-skeleton">
        <div v-for="itemIndex in itemsPerGroup" :key="'list-' + itemIndex" class="list-item-skeleton">
          <!-- Desktop: Single Row (4 Columns) -->
          <div class="desktop-skeleton-row">
            <!-- Section 1: Car Info (Image + Name/Details) -->
            <div class="skeleton-section-car">
              <b-skeleton width="80px" height="50px" :animated="true"></b-skeleton>
              <div class="skeleton-car-details">
                <b-skeleton width="140px" height="16px" :animated="true"></b-skeleton>
                <b-skeleton width="100px" height="12px" :animated="true"></b-skeleton>
              </div>
            </div>

            <!-- Section 2: Tags -->
            <div class="skeleton-section-tags">
              <b-skeleton width="65px" height="22px" :animated="true"></b-skeleton>
              <b-skeleton width="55px" height="22px" :animated="true"></b-skeleton>
            </div>

            <!-- Section 3: Price Info -->
            <div class="skeleton-section-price">
              <b-skeleton width="50px" height="18px" :animated="true"></b-skeleton>
              <b-skeleton width="70px" height="14px" :animated="true"></b-skeleton>
            </div>

            <!-- Section 4: Actions -->
            <div class="skeleton-section-actions">
              <b-skeleton width="85px" height="30px" :animated="true"></b-skeleton>
              <b-skeleton width="60px" height="30px" :animated="true"></b-skeleton>
            </div>
          </div>

          <!-- Mobile: Stacked Layout -->
          <div class="mobile-skeleton-layout">
            <div class="mobile-skeleton-top">
              <b-skeleton width="70px" height="45px" :animated="true"></b-skeleton>
              <div class="mobile-skeleton-info">
                <b-skeleton width="130px" height="14px" :animated="true"></b-skeleton>
                <b-skeleton width="100px" height="11px" :animated="true"></b-skeleton>
                <div class="skeleton-tags">
                  <b-skeleton width="50px" height="18px" :animated="true"></b-skeleton>
                  <b-skeleton width="45px" height="18px" :animated="true"></b-skeleton>
                </div>
              </div>
            </div>
            <div class="mobile-skeleton-price-buttons">
              <div class="mobile-skeleton-price">
                <b-skeleton width="55px" height="16px" :animated="true"></b-skeleton>
                <b-skeleton width="70px" height="12px" :animated="true"></b-skeleton>
              </div>
              <div class="mobile-skeleton-buttons">
                <b-skeleton width="85px" height="28px" :animated="true"></b-skeleton>
                <b-skeleton width="55px" height="28px" :animated="true"></b-skeleton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Card View Skeleton -->
      <div v-else class="card-skeleton">
        <div class="carousel-skeleton-scroll">
          <div v-for="itemIndex in itemsPerGroup" :key="'card-' + itemIndex" class="card-item-skeleton">
            <!-- Car Image -->
            <b-skeleton width="100%" height="100px" :animated="true"></b-skeleton>
            <!-- Car Name -->
            <b-skeleton width="75%" height="15px" :animated="true"></b-skeleton>
            <!-- Car Details -->
            <b-skeleton width="60%" height="11px" :animated="true"></b-skeleton>
            <!-- Tags -->
            <div class="skeleton-tags">
              <b-skeleton width="55px" height="18px" :animated="true"></b-skeleton>
              <b-skeleton width="45px" height="18px" :animated="true"></b-skeleton>
            </div>
            <!-- Price & Buttons -->
            <div class="card-skeleton-price-buttons">
              <div class="card-skeleton-price">
                <b-skeleton width="50px" height="16px" :animated="true"></b-skeleton>
                <b-skeleton width="65px" height="11px" :animated="true"></b-skeleton>
              </div>
              <div class="card-skeleton-buttons">
                <b-skeleton width="85px" height="26px" :animated="true"></b-skeleton>
                <b-skeleton width="55px" height="26px" :animated="true"></b-skeleton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CarResultsSkeleton',
  props: {
    viewMode: {
      type: String,
      default: 'list' // 'list' or 'card'
    },
    groupCount: {
      type: Number,
      default: 3 // Number of groups to show
    },
    itemsPerGroup: {
      type: Number,
      default: 2 // Number of items per group
    }
  }
}
</script>

<style scoped>
.car-results-skeleton {
  width: 100%;
}

/* Controls Row Skeleton - Matches car-controls-row */
.controls-row-skeleton {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.left-controls-skeleton {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

/* Grouping Toggle Skeleton - Matches grouping-toggle */
.grouping-toggle-skeleton {
  display: flex;
  gap: 5px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
}

/* View Toggle Skeleton - Matches view-toggle */
.view-toggle-skeleton {
  display: flex;
  gap: 5px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
}

/* Skeleton Button - Matches actual button dimensions */
.skeleton-button {
  width: 85px;
  height: 36px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Group Navigation Tabs Skeleton - Matches group-navigation-tabs */
.group-nav-tabs-skeleton {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  overflow-x: auto;
}

/* Skeleton Nav Tab - Matches group-nav-tab */
.skeleton-nav-tab {
  width: 120px;
  height: 36px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  border: 1px solid #ddd;
}

/* Responsive - Mobile */
@media screen and (max-width: 768px) {
  .controls-row-skeleton {
    flex-direction: column;
    align-items: flex-start;
  }

  .skeleton-button {
    width: 75px;
    height: 32px;
  }
}

@media screen and (max-width: 480px) {
  .grouping-toggle-skeleton,
  .view-toggle-skeleton {
    justify-content: left;
  }
}

/* Group Skeleton */
.group-skeleton {
  margin-bottom: 32px;
}

.group-header-skeleton {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
}

.group-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* List View Skeleton */
.list-skeleton {
  background: white;
}

.list-item-skeleton {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

/* Desktop: Single Row (3 Sections) */
.desktop-skeleton-row {
  display: none;
}

.mobile-skeleton-layout {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-skeleton-top {
  display: flex;
  gap: 12px;
}

.mobile-skeleton-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-tags {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.mobile-skeleton-price-buttons {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.mobile-skeleton-price {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-skeleton-buttons {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

@media screen and (min-width: 1024px) {
  .desktop-skeleton-row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .mobile-skeleton-layout {
    display: none;
  }

  /* Section 1: Car Info (Image + Name/Details) */
  .skeleton-section-car {
    flex: 2;
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .skeleton-car-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  /* Section 2: Tags */
  .skeleton-section-tags {
    flex: 1;
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
  }

  /* Section 3: Price Info */
  .skeleton-section-price {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Section 4: Actions */
  .skeleton-section-actions {
    flex: 1.2;
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
  }
}

/* Card View Skeleton */
.card-skeleton {
  margin-top: 16px;
}

.carousel-skeleton-scroll {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 0;
}

.card-item-skeleton {
  min-width: 260px;
  max-width: 260px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 16px;
  background: white;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media screen and (min-width: 1024px) {
  .card-item-skeleton {
    min-width: 260px;
    max-width: 260px;
  }
}

.card-skeleton-price-buttons {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.card-skeleton-price {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-skeleton-buttons {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}
</style>

